import { ethers, TypedDataDomain, TypedDataField } from 'ethers';
import { RPC_ORAI_EVM } from '@/rpcConfig';
import { getEvmProvider } from '@/hooks/useConnectEvm';
import axios from 'axios';
import { config } from './util';
import { ethermint } from '@oraichain/proto/dist/codegen/ethermint/bundle';
import { toBech32 } from '@cosmjs/encoding';

//   chainId: 108160679, // mainnet
//   chainId: 4143398064, // testnet

const baseUrl = 'https://evm-mapping-relayer.orai.network';
const rpcEndpoint = 'https://testnet-v2.rpc.orai.io';

interface MappingEvmValue {
  Message: string;
  URL: string;
  Network: string;
  Version: string;
  Nonce?: string;
  Account: string;
}

interface MappingEvmDto {
  signature: string;
  value: MappingEvmValue;
}

let provider: ethers.JsonRpcProvider;
export class Web3MappingEvmInteraction {
  constructor() { }

  getJsonProvider = (rpc = RPC_ORAI_EVM) => {
    if (!provider) {
      provider = new ethers.JsonRpcProvider(rpc);
    }

    return provider;
  };

  getSigner = async () => {
    const ethereum = getEvmProvider();
    const provider = new ethers.BrowserProvider(ethereum);
    const signer = await provider.getSigner();
    return signer;
  };

  signTypedData = async (
    domain: TypedDataDomain,
    types: Record<string, Array<TypedDataField>>,
    _value: Record<string, any>,
  ) => {
    try {
      const signer = await this.getSigner();
      const userAddress = await signer.getAddress();
      console.log('Connected wallet address:', userAddress);
      const signature = await signer.signTypedData(domain, types, _value);
      console.log('Signature:', signature);
      return signature;
    } catch (err) {
      console.log('error signTypedData', err);
      throw err;
    }
  };

  getAuthMessage = async (params: MappingEvmValue) => {
    try {
      const response = await axios.get(`${baseUrl}/auth/message?value[Message]=${params.Message}&value[URL]=${params.URL}&value[Network]=${params.Network}&value[Version]=${params.Version}&value[Account]=${params.Account}`, {
        ...config,
      });
      console.log("response:", response.data);
      return response.data;
    } catch (err) {
      console.log('error getAuthMessage', err);
      throw err;
    }
  };

  postAuthMessage = async (data: MappingEvmDto) => {
    try {
      const response = await axios.post(
        `${baseUrl}/auth/mapping`,
        data,
        config,
      );
      return response;
    } catch (err) {
      console.log('error postAuthMessage', err);
      throw err;
    }
  };

  mappingAddress = async (wallet: string) => {
    try {
      const { isMapping } = await this.queryMappedAddress({ evmAddress: wallet });
      if (!isMapping) {
        const { message } = await this.getAuthMessage({
          "Message": "Welcome to Oraichain App. Signing is the only way we can truly know that you are the owner of the wallet you are connecting. Signing is a safe, gas-less transaction that does not in any way give Oraichain App permission to perform any transactions with your wallet",
          "URL": window.location.href,
          // "Network": "0x67266a7",
          "Network": `0xF6F73CB0`,
          "Account": wallet,
          "Version": "1",
        });

        const signature = await this.signTypedData(message.domain, message.types, message.value);
        const { data } = await this.postAuthMessage({
          signature,
          value: {
            ...message.value,
            "Account": wallet,
          }
        })
        const { address, isMapping } = await this.queryMappedAddress({ evmAddress: wallet });
        return {
          ...data,
          address,
          isMapping,
        }
      }
      return;
    } catch (error) {
      console.log('error mappingAddress', error);
      throw error;
    }
  }

  queryMappedAddress = async ({ cosmosAddress, evmAddress, rpc = rpcEndpoint }: {
    cosmosAddress?: string,
    evmAddress?: string,
    rpc?: string
  }) => {
    console.log('Params:', evmAddress, cosmosAddress);
    try {
      const queryClient = await ethermint.ClientFactory.createRPCQueryClient({
        rpcEndpoint: rpc,
      });

      let response;
      if (cosmosAddress) {
        response = await queryClient.ethermint.evm.v1.mappedEvmAddress({
          cosmosAddress: cosmosAddress,
        });
        return {
          address: response.evmAddress,
          isMapping: true
        };
      }

      response = await queryClient.ethermint.evm.v1.mappedCosmosAddress({
        evmAddress: evmAddress,
      });

      const beforeMappedCosmosAddress = toBech32("orai", Buffer.from(evmAddress.slice(2), "hex"))

      return {
        address: response.cosmosAddress,
        isMapping: beforeMappedCosmosAddress !== response.cosmosAddress
      };
    } catch (error) {
      console.error('Error querying mapped address:', error);
      return {
        address: '',
        isMapping: false
      };
    }
  };

  suggestChain = async () => {
    const networkData = {
      chainId: '0x67266a7',
      chainName: 'Oraichain EVM',
      rpcUrls: ['https://evm.orai.io'],
      nativeCurrency: {
        name: 'ORAI',
        symbol: 'ORAI',
        decimals: 18,
      },
      blockExplorerUrls: [''],
    };

    try {
      const chainId = await window.ethereum.request({
        method: 'eth_chainId',
      });

      if (chainId !== '0x67266a7') {
        await window.ethereum.request({
          method: 'wallet_addEthereumChain',
          params: [networkData],
        });
      }
    } catch (err) {
      console.log('error suggestChain', err);
      throw err;
    }
  };

  switchChain = async (chainId = '0x67266a7') => {
    try {
      await window.ethereum.request({
        method: 'wallet_switchEthereumChain',
        params: [{ chainId }],
      });
    } catch (err) {
      console.log('error switchChain', err);
      throw err;
    }
  };
}
